import React from 'react'
import styled from 'styled-components'
import { Modal, Tag, Empty, Button } from 'antd'
import { MarkdownVersion } from 'types/legalDocument'
import dayjs from 'dayjs'

interface Props {
  open: boolean
  versions: MarkdownVersion[]
  reapplyingVersion: number | null
  canReapply: boolean
  onReapply: (version: MarkdownVersion) => void
  onClose: () => void
}

const MarkdownHistoryModal = ({
  open,
  versions,
  reapplyingVersion,
  canReapply,
  onReapply,
  onClose,
}: Props): JSX.Element => {
  const latestVersion =
    versions.length > 0 ? versions[versions.length - 1].version : null

  return (
    <Modal
      title="Historique des versions Markdown"
      open={open}
      onCancel={onClose}
      footer={null}
      width="80vw"
      style={{ top: 20 }}
      styles={{
        body: { maxHeight: '75vh', overflowY: 'auto', padding: '16px 24px' },
      }}
    >
      {versions.length === 0 ? (
        <Empty description="Aucune version disponible" />
      ) : (
        <VersionList>
          {[...versions].reverse().map(v => (
            <VersionCard key={v.version}>
              <VersionHeader>
                <Tag color="blue">v{v.version}</Tag>
                {v.created_at && (
                  <DateLabel>
                    {dayjs(v.created_at).format('DD/MM/YYYY HH:mm')}
                  </DateLabel>
                )}
                {(v.author_name || v.author_id) && (
                  <AuthorLabel>
                    {v.author_name ?? `auteur #${v.author_id}`}
                  </AuthorLabel>
                )}
                <ReapplyButton>
                  {v.version !== latestVersion && (
                    <Button
                      size="small"
                      loading={reapplyingVersion === v.version}
                      disabled={
                        !canReapply ||
                        (reapplyingVersion !== null &&
                          reapplyingVersion !== v.version)
                      }
                      onClick={() => onReapply(v)}
                    >
                      Réappliquer
                    </Button>
                  )}
                </ReapplyButton>
              </VersionHeader>
              <Pre>{v.content}</Pre>
            </VersionCard>
          ))}
        </VersionList>
      )}
    </Modal>
  )
}

const VersionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const VersionCard = styled.div`
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  overflow: hidden;
`

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f7f8f9;
  border-bottom: 1px solid #e6e8eb;
`

const DateLabel = styled.span`
  font-size: 12px;
  color: #555;
`

const AuthorLabel = styled.span`
  font-size: 12px;
  color: #888;
`

const ReapplyButton = styled.div`
  margin-left: auto;
`

const Pre = styled.pre`
  margin: 0;
  padding: 12px;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fff;
  max-height: 300px;
  overflow-y: auto;
`

export default MarkdownHistoryModal
